import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import axios from "axios";
import Cookies from "js-cookie";
import { Tooltip as InfoTooltip } from "../ui/Tooltip";
import {
  Target,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  Award,
  Flag
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell,
  RadialBarChart,
  RadialBar
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const StrategicAlignment = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [activeTab, setActiveTab] = useState("objectives");
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [objectiveDetails, setObjectiveDetails] = useState(null);
  const [initiativeDetails, setInitiativeDetails] = useState(null);
  
  const [objectivesData, setObjectivesData] = useState(null);
  const [initiativesData, setInitiativesData] = useState(null);
  const [kpisData, setKpisData] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("enakid_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      const [objectivesRes, initiativesRes, kpisRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/actual-data/strategic-objectives/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/strategic-initiatives-portfolio/${currentPlan.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/actual-data/strategic-kpis/${currentPlan.id}`, { headers })
      ]);
      
      setObjectivesData(objectivesRes.data);
      setInitiativesData(initiativesRes.data);
      setKpisData(kpisRes.data);
    } catch (err) {
      console.error("Error fetching strategic alignment:", err);
      setError(err.response?.data?.detail || "Failed to load strategic alignment");
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectiveDetails = async (objectiveId) => {
    try {
      const token = Cookies.get("enakid_token");
      const response = await axios.get(
        `${BACKEND_URL}/api/actual-data/objective-details/${objectiveId}?plan_id=${currentPlan.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      setObjectiveDetails(response.data);
    } catch (err) {
      console.error("Error fetching objective details:", err);
    }
  };

  const fetchInitiativeDetails = async (initiativeId) => {
    try {
      const token = Cookies.get("enakid_token");
      const response = await axios.get(
        `${BACKEND_URL}/api/actual-data/initiative-details/${initiativeId}?plan_id=${currentPlan.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      setInitiativeDetails(response.data);
    } catch (err) {
      console.error("Error fetching initiative details:", err);
    }
  };

  const handleObjectiveClick = (obj) => {
    setSelectedObjective(obj);
    fetchObjectiveDetails(obj.objective_id);
  };

  const handleInitiativeClick = (init) => {
    setSelectedInitiative(init);
    fetchInitiativeDetails(init.initiative_id);
  };

  const formatCurrency = (value) => {
    return formatCurrencyContext(value || 0, currentPlan?.currency);
  };

  const getStatusBadge = (status) => {
    const configs = {
      on_track: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "On Track" },
      in_progress: { color: "bg-blue-100 text-blue-800", icon: Clock, label: "In Progress" },
      at_risk: { color: "bg-yellow-100 text-yellow-800", icon: AlertCircle, label: "At Risk" },
      delayed: { color: "bg-red-100 text-red-800", icon: AlertCircle, label: "Delayed" },
      completed: { color: "bg-purple-100 text-purple-800", icon: CheckCircle, label: "Completed" },
      not_started: { color: "bg-gray-100 text-gray-800", icon: Clock, label: "Not Started" }
    };
    
    const config = configs[status] || configs.not_started;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading Strategic Alignment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <p className="font-medium">{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!objectivesData?.objective_analysis?.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Strategic Data Available</h3>
              <p className="text-gray-600">Set up objectives and initiatives in Strategic Framework.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <InfoTooltip content="Track performance vs strategic objectives, initiatives, and KPIs. Shows: objective progress & health, initiative execution & budget, KPI performance vs targets. Red flags: objectives <50% progress, initiatives >110% budget, declining KPIs.">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Target className="w-8 h-8 text-blue-600" />
                Strategic Alignment
              </h1>
            </InfoTooltip>
            <p className="text-gray-600 mt-2">
              Track objectives, initiatives, and strategic KPIs
            </p>
          </div>
          <button
            onClick={fetchAllData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <InfoTooltip content="View objectives by progress, status, and linked initiatives/products/costs. Status: Green (>80% progress), Blue (50-80%), Yellow (30-50%), Red (<30%). Click any card to drill down into details.">
              <TabsTrigger value="objectives">By Objectives</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="View initiative portfolio with budget utilization, timelines, and linked products/costs. Shows budget vs actual spend. Utilization >100% = over budget (investigate immediately).">
              <TabsTrigger value="initiatives">By Initiatives</TabsTrigger>
            </InfoTooltip>
            <InfoTooltip content="Monitor Key Performance Indicators vs targets with trend analysis. Shows Plan vs Actual for each KPI, variance, trend direction (↑/↓), and 12-month revenue trend. Red flags: multiple KPIs trending down, large negative variances.">
              <TabsTrigger value="kpis">Strategic KPIs</TabsTrigger>
            </InfoTooltip>
          </TabsList>

          {/* Tab 1: By Objectives */}
          <TabsContent value="objectives" className="space-y-6">
            {!selectedObjective ? (
              <>
                {/* Radar Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Spider/radar chart showing progress (0-100%) across all objectives. Each axis = one objective. Larger blue area = more completion. Symmetrical shape = balanced progress. Lopsided = some objectives ahead, others behind. Identify which objectives need more resources.">
                      <CardTitle>Multi-Objective Progress</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Spider chart showing progress across all objectives</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={objectivesData.objective_analysis}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="objective_name" />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} />
                        <Radar name="Progress" dataKey="progress_pct" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                        <Tooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Traffic Light Grid */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Traffic light view of objective health. Colors: Green (>80% progress, on track), Blue (50-80%, in progress), Yellow (30-50%, at risk), Red (<30%, delayed). Click any card to drill into full details including initiatives, products, costs, and progress history.">
                      <CardTitle>Objective Status Grid</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {objectivesData.objective_analysis.map((obj, idx) => (
                        <div 
                          key={idx} 
                          className="p-4 border-2 rounded-lg cursor-pointer hover:shadow-lg transition-shadow"
                          style={{
                            borderColor: obj.status === 'on_track' ? '#10b981' : obj.status === 'in_progress' ? '#3b82f6' : obj.status === 'at_risk' ? '#f59e0b' : '#ef4444',
                            backgroundColor: obj.status === 'on_track' ? '#f0fdf4' : obj.status === 'in_progress' ? '#eff6ff' : obj.status === 'at_risk' ? '#fffbeb' : '#fef2f2'
                          }}
                          onClick={() => handleObjectiveClick(obj)}
                        >
                          <h4 className="font-semibold text-lg mb-2">{obj.objective_name}</h4>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Progress:</span>
                            <span className="font-bold text-2xl">{obj.progress_pct.toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full"
                              style={{
                                width: `${obj.progress_pct}%`,
                                backgroundColor: obj.status === 'on_track' ? '#10b981' : obj.status === 'in_progress' ? '#3b82f6' : obj.status === 'at_risk' ? '#f59e0b' : '#ef4444'
                              }}
                            ></div>
                          </div>
                          <div className="mt-3 flex justify-between text-sm">
                            <span className="text-gray-600">{obj.total_initiatives} initiatives</span>
                            {getStatusBadge(obj.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Objectives Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Comprehensive breakdown showing: Objective Name, Owner, Status (health indicator), Progress %, Active Initiatives count, Actions. Formula: Progress % = weighted average of linked initiatives. Example: 3 initiatives at 80%, 60%, 40% = 60% objective progress. Red flags: 0 initiatives (no execution plan), >5 initiatives (too complex), progress <30% (delayed).">
                      <CardTitle>Objectives Details</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Objective</th>
                            <th className="text-left p-3 font-semibold">Target</th>
                            <th className="text-right p-3 font-semibold">Progress</th>
                            <th className="text-center p-3 font-semibold">Initiatives</th>
                            <th className="text-center p-3 font-semibold">Status</th>
                            <th className="text-center p-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {objectivesData.objective_analysis.map((obj, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{obj.objective_name}</td>
                              <td className="p-3">{obj.target}</td>
                              <td className="p-3 text-right">
                                <span className="font-bold">{obj.progress_pct.toFixed(0)}%</span>
                              </td>
                              <td className="p-3 text-center">{obj.total_initiatives}</td>
                              <td className="p-3 text-center">{getStatusBadge(obj.status)}</td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => handleObjectiveClick(obj)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  View Details →
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Objective Deep Dive */
              <>
                <button
                  onClick={() => setSelectedObjective(null)}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                >
                  ← Back to Objectives
                </button>
                
                <Card className="border-2 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-2xl">{selectedObjective.objective_name}</CardTitle>
                    <CardDescription>{selectedObjective.target}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div className="text-center">
                        <ResponsiveContainer width="100%" height={150}>
                          <RadialBarChart 
                            cx="50%" 
                            cy="50%" 
                            innerRadius="60%" 
                            outerRadius="100%"
                            data={[{ value: selectedObjective.progress_pct, fill: '#3b82f6' }]}
                            startAngle={180}
                            endAngle={0}
                          >
                            <RadialBar background dataKey="value" />
                          </RadialBarChart>
                        </ResponsiveContainer>
                        <p className="font-bold text-2xl">{selectedObjective.progress_pct.toFixed(0)}%</p>
                        <p className="text-sm text-gray-600">Complete</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-2">Initiatives</h4>
                        <div className="space-y-1">
                          <p className="text-sm">Total: {selectedObjective.total_initiatives}</p>
                          <p className="text-sm text-green-600">Completed: {selectedObjective.completed_initiatives}</p>
                          <p className="text-sm text-blue-600">In Progress: {selectedObjective.in_progress_initiatives}</p>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-2">Financial Impact</h4>
                        <div className="space-y-1">
                          <p className="text-sm">Revenue: {formatCurrency(selectedObjective.revenue_impact)}</p>
                          <p className="text-sm">Cost: {formatCurrency(selectedObjective.cost_impact)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Initiative Breakdown */}
                    <h4 className="font-semibold text-lg mb-3">Linked Initiatives</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={selectedObjective.linked_initiatives}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="initiative_name" angle={-45} textAnchor="end" height={100} />
                        <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                      </BarChart>
                    </ResponsiveContainer>

                    {/* Initiatives Table */}
                    <div className="mt-6">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Initiative</th>
                            <th className="text-left p-2">Owner</th>
                            <th className="text-right p-2">Budget</th>
                            <th className="text-center p-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedObjective.linked_initiatives.map((init, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{init.initiative_name}</td>
                              <td className="p-2">{init.owner}</td>
                              <td className="p-2 text-right">{formatCurrency(init.budget)}</td>
                              <td className="p-2 text-center">
                                <Badge>{init.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Linked Products/Costs Performance */}
                {objectiveDetails && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle>Linked Products Performance</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {objectiveDetails.product_performance && objectiveDetails.product_performance.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-gray-50">
                                  <th className="text-left p-3 font-semibold">Product/Service</th>
                                  <th className="text-left p-3 font-semibold">Type</th>
                                  <th className="text-left p-3 font-semibold">SKU</th>
                                  <th className="text-right p-3 font-semibold">Planned</th>
                                  <th className="text-right p-3 font-semibold">Actual</th>
                                  <th className="text-right p-3 font-semibold">Variance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {objectiveDetails.product_performance.map((prod, idx) => (
                                  <tr key={idx} className="border-b hover:bg-gray-50">
                                    <td className="p-3">{prod.product_name}</td>
                                    <td className="p-3">
                                      <Badge className={prod.product_type === 'services' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                                        {prod.product_type === 'services' ? 'Service' : 'Product'}
                                      </Badge>
                                    </td>
                                    <td className="p-3">{prod.product_sku}</td>
                                    <td className="p-3 text-right">{formatCurrency(prod.planned_revenue)}</td>
                                    <td className="p-3 text-right font-medium">{formatCurrency(prod.actual_revenue)}</td>
                                    <td className={`p-3 text-right font-bold ${prod.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {prod.variance_pct > 0 ? "+" : ""}{prod.variance_pct.toFixed(1)}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-600 text-center py-4">No linked products found</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Linked Costs Performance</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {objectiveDetails.cost_performance && objectiveDetails.cost_performance.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-gray-50">
                                  <th className="text-left p-3 font-semibold">Item</th>
                                  <th className="text-left p-3 font-semibold">Category</th>
                                  <th className="text-right p-3 font-semibold">Budgeted</th>
                                  <th className="text-right p-3 font-semibold">Actual</th>
                                  <th className="text-right p-3 font-semibold">Variance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {objectiveDetails.cost_performance.map((cost, idx) => (
                                  <tr key={idx} className="border-b hover:bg-gray-50">
                                    <td className="p-3">{cost.item_name}</td>
                                    <td className="p-3">{cost.category}</td>
                                    <td className="p-3 text-right">{formatCurrency(cost.budgeted)}</td>
                                    <td className="p-3 text-right font-medium">{formatCurrency(cost.actual)}</td>
                                    <td className={`p-3 text-right font-bold ${cost.variance_pct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {cost.variance_pct > 0 ? "+" : ""}{cost.variance_pct.toFixed(1)}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-600 text-center py-4">No linked costs found</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Notes & Updates */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Notes & Updates</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                            <p className="text-sm font-medium text-blue-900">Latest Update</p>
                            <p className="text-sm text-blue-700 mt-1">
                              Objective tracking active. {selectedObjective.completed_initiatives} of {selectedObjective.total_initiatives} initiatives completed.
                            </p>
                            <p className="text-xs text-blue-600 mt-2">Status: {selectedObjective.status}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded">
                            <p className="text-sm text-gray-700">
                              <strong>Target:</strong> {selectedObjective.target}
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>Timeline:</strong> {selectedObjective.timeline || 'Not specified'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* Tab 2: By Initiatives */}
          <TabsContent value="initiatives" className="space-y-6">
            {!selectedInitiative ? (
              <>
                {/* Gantt Chart */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Gantt-style view showing initiative timelines and budget utilization. Bar fill % = budget spent. Colors: Purple (completed), Green (on track), Yellow (at risk), Red (delayed). Bar >100% filled = over budget (critical!). Use for resource planning and identifying initiatives approaching budget limits.">
                      <CardTitle>Initiative Timeline (Gantt View)</CardTitle>
                    </InfoTooltip>
                    <CardDescription>Visual timeline of all initiatives</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {initiativesData.initiative_portfolio.map((init, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-40 text-sm font-medium truncate">{init.initiative_name}</div>
                          <div className="flex-1 relative h-8 bg-gray-100 rounded">
                            <div 
                              className={`absolute h-full rounded ${
                                init.status === 'completed' ? 'bg-purple-500' :
                                init.status === 'on_track' ? 'bg-green-500' :
                                init.status === 'at_risk' ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ 
                                left: '10%', 
                                width: `${Math.min(init.budget_utilization, 100)}%` 
                              }}
                            >
                              <span className="text-xs text-white px-2 leading-8">
                                {init.budget_utilization.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-32 text-xs text-gray-600">{init.timeline}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Status Pie Chart */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Pie chart showing portfolio breakdown by status. Slices: Green (on track), Yellow (at risk), Red (delayed), Purple (completed). Ideal: >60% on track, <20% at risk, <10% delayed. Warning: <40% on track = systemic execution problems. 0% completed = no delivery velocity.">
                        <CardTitle>Initiative Status Distribution</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'On Track', value: initiativesData.status_summary.on_track },
                              { name: 'At Risk', value: initiativesData.status_summary.at_risk },
                              { name: 'Delayed', value: initiativesData.status_summary.delayed },
                              { name: 'Completed', value: initiativesData.status_summary.completed }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={(entry) => `${entry.name}: ${entry.value}`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#10b981" />
                            <Cell fill="#f59e0b" />
                            <Cell fill="#ef4444" />
                            <Cell fill="#8b5cf6" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Budget Burndown */}
                  <Card>
                    <CardHeader>
                      <InfoTooltip content="Budget vs Actual comparison for each initiative. Light blue = authorized budget, Dark blue = actual spent. Read: dark < light = under budget, dark = light = on budget, dark > light = over budget (investigate). Formula: Utilization % = (Actual / Budget) × 100. Red flag: >110% utilization.">
                        <CardTitle>Budget Utilization</CardTitle>
                      </InfoTooltip>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={initiativesData.initiative_portfolio}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="initiative_name" angle={-45} textAnchor="end" height={100} />
                          <YAxis tickFormatter={(value) => formatCurrencyContext(value, currentPlan?.currency, null, true)} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="budget" fill="#93c5fd" name="Budget" />
                          <Bar dataKey="actual_spent" fill="#3b82f6" name="Actual Spent" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Initiatives Table */}
                <Card>
                  <CardHeader>
                    <InfoTooltip content="Initiative portfolio scorecard showing: Name, Owner, Timeline, Budget, Actual, Utilization %, Status, Actions. Formula: Utilization % = (Actual/Budget) × 100. Ranges: <80% (under budget), 80-100% (on track), >100% (over budget - critical). Red flags: >110% utilization, no owner assigned, timeline >12 months without phases.">
                      <CardTitle>Initiative Portfolio</CardTitle>
                    </InfoTooltip>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-3 font-semibold">Initiative</th>
                            <th className="text-left p-3 font-semibold">Owner</th>
                            <th className="text-left p-3 font-semibold">Timeline</th>
                            <th className="text-right p-3 font-semibold">Budget</th>
                            <th className="text-right p-3 font-semibold">Actual</th>
                            <th className="text-right p-3 font-semibold">Utilization</th>
                            <th className="text-center p-3 font-semibold">Status</th>
                            <th className="text-center p-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {initiativesData.initiative_portfolio.map((init, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="p-3 font-medium">{init.initiative_name}</td>
                              <td className="p-3">{init.owner}</td>
                              <td className="p-3 text-sm">{init.timeline}</td>
                              <td className="p-3 text-right">{formatCurrency(init.budget)}</td>
                              <td className="p-3 text-right">{formatCurrency(init.actual_spent)}</td>
                              <td className="p-3 text-right">
                                <span className={`font-bold ${init.budget_utilization > 100 ? 'text-red-600' : 'text-green-600'}`}>
                                  {init.budget_utilization.toFixed(0)}%
                                </span>
                              </td>
                              <td className="p-3 text-center">{getStatusBadge(init.status)}</td>
                              <td className="p-3 text-center">
                                <button
                                  onClick={() => handleInitiativeClick(init)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  View Details →
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* Initiative Deep Dive */
              <>
                <button
                  onClick={() => {
                    setSelectedInitiative(null);
                    setInitiativeDetails(null);
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                >
                  ← Back to Initiatives
                </button>

                {initiativeDetails && (
                  <>
                    <Card className="border-2 border-blue-200">
                      <CardHeader>
                        <CardTitle className="text-2xl">{initiativeDetails.initiative.initiative_name}</CardTitle>
                        <CardDescription>
                          Owner: {initiativeDetails.initiative.owner} | Status: {initiativeDetails.initiative.status}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          {/* Budget Utilization Gauge */}
                          <div className="text-center">
                            <h4 className="font-semibold mb-3">Budget Utilization</h4>
                            <ResponsiveContainer width="100%" height={150}>
                              <RadialBarChart 
                                cx="50%" 
                                cy="50%" 
                                innerRadius="60%" 
                                outerRadius="100%"
                                data={[{ 
                                  value: Math.min(initiativeDetails.initiative.budget_utilization, 100), 
                                  fill: initiativeDetails.initiative.budget_utilization > 100 ? '#ef4444' : '#3b82f6' 
                                }]}
                                startAngle={180}
                                endAngle={0}
                              >
                                <RadialBar background dataKey="value" />
                              </RadialBarChart>
                            </ResponsiveContainer>
                            <p className="font-bold text-2xl">{initiativeDetails.initiative.budget_utilization.toFixed(0)}%</p>
                            <p className="text-sm text-gray-600">
                              {formatCurrency(initiativeDetails.initiative.actual_spent)} of {formatCurrency(initiativeDetails.initiative.budget)}
                            </p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold mb-2">Timeline</h4>
                            <div className="space-y-1">
                              <p className="text-sm">Start: {initiativeDetails.initiative.start_date || 'Not set'}</p>
                              <p className="text-sm">End: {initiativeDetails.initiative.end_date || 'Not set'}</p>
                              <p className="text-sm mt-2 text-gray-600">{initiativeDetails.initiative.description || 'No description available'}</p>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold mb-2">Financial Summary</h4>
                            <div className="space-y-1">
                              <p className="text-sm">Budget: {formatCurrency(initiativeDetails.initiative.budget)}</p>
                              <p className="text-sm">Spent: {formatCurrency(initiativeDetails.initiative.actual_spent)}</p>
                              <p className="text-sm">
                                Remaining: {formatCurrency(initiativeDetails.initiative.budget - initiativeDetails.initiative.actual_spent)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Product Performance */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Linked Product Performance</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {initiativeDetails.linked_products && initiativeDetails.linked_products.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={initiativeDetails.linked_products}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="product_sku" angle={-45} textAnchor="end" height={100} />
                                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Bar dataKey="planned_revenue" fill="#93c5fd" name="Planned" />
                                <Bar dataKey="actual_revenue" fill="#10b981" name="Actual" />
                              </BarChart>
                            </ResponsiveContainer>
                            <div className="mt-4 overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b bg-gray-50">
                                    <th className="text-left p-2">Product/Service</th>
                                    <th className="text-left p-2">Type</th>
                                    <th className="text-right p-2">Planned</th>
                                    <th className="text-right p-2">Actual</th>
                                    <th className="text-right p-2">Variance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {initiativeDetails.linked_products.map((prod, idx) => (
                                    <tr key={idx} className="border-b">
                                      <td className="p-2">{prod.product_name}</td>
                                      <td className="p-2">
                                        <Badge className={prod.product_type === 'services' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                                          {prod.product_type === 'services' ? 'Service' : 'Product'}
                                        </Badge>
                                      </td>
                                      <td className="p-2 text-right">{formatCurrency(prod.planned_revenue)}</td>
                                      <td className="p-2 text-right">{formatCurrency(prod.actual_revenue)}</td>
                                      <td className={`p-2 text-right font-bold ${prod.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {prod.variance_pct > 0 ? '+' : ''}{prod.variance_pct.toFixed(1)}%
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : (
                          <p className="text-gray-600 text-center py-4">No linked products found</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Cost Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Cost Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={initiativeDetails.cost_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" angle={-45} textAnchor="end" height={80} />
                            <YAxis tickFormatter={(value) => formatCurrency(value)} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                            <Bar dataKey="budgeted" fill="#93c5fd" name="Budgeted" />
                            <Bar dataKey="actual" fill="#ef4444" name="Actual" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Milestones & Actuals */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Milestones & Actuals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {initiativeDetails.milestones.map((milestone, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 border rounded">
                              <div className={`w-3 h-3 rounded-full ${
                                milestone.status === 'completed' ? 'bg-green-500' :
                                milestone.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-300'
                              }`}></div>
                              <div className="flex-1">
                                <p className="font-medium">{milestone.milestone}</p>
                                <p className="text-sm text-gray-600">
                                  Planned: {milestone.planned_date || 'TBD'} 
                                  {milestone.actual_date && ` | Actual: ${milestone.actual_date}`}
                                </p>
                              </div>
                              <Badge className={
                                milestone.status === 'completed' ? 'bg-green-100 text-green-800' :
                                milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {milestone.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* Tab 3: Strategic KPIs */}
          <TabsContent value="kpis" className="space-y-6">
            {/* KPI Scorecard */}
            <Card>
              <CardHeader>
                <InfoTooltip content="KPI grid showing Actual vs Target with trend arrows. Each card: KPI name, actual value, target, variance. Arrows: Green ↑ (improving), Red ↓ (declining). For costs: ↓ is good, ↑ is bad. Use for executive dashboards and board reporting. Red flags: multiple KPIs declining, large negative variances.">
                  <CardTitle>KPI Scorecard</CardTitle>
                </InfoTooltip>
                <CardDescription>Plan vs Actual performance across key metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {kpisData.kpi_analysis.map((kpi, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-sm mb-2">{kpi.kpi_name}</h4>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold">
                          {kpi.unit === 'currency' ? formatCurrency(kpi.actual) : `${kpi.actual.toFixed(1)}${kpi.unit}`}
                        </span>
                        {kpi.trend === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Target: {kpi.unit === 'currency' ? formatCurrency(kpi.target) : `${kpi.target}${kpi.unit}`}
                      </p>
                      <p className={`text-xs mt-1 ${kpi.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.unit === 'currency' ? formatCurrency(Math.abs(kpi.variance)) : `${Math.abs(kpi.variance).toFixed(1)}${kpi.unit}`}
                        {kpi.variance >= 0 ? ' above' : ' below'} target
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Trend Lines */}
            <Card>
              <CardHeader>
                <InfoTooltip content="12-month line chart showing monthly revenue trajectory. Read: line up = revenue growing, flat = stagnant, down = declining (urgent action needed). Use to identify growth trends, seasonality patterns, and inflection points. Compare to forecast to check if meeting plan.">
                  <CardTitle>Revenue Trend (12 Months)</CardTitle>
                </InfoTooltip>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={kpisData.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Monthly Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* KPI Table */}
            <Card>
              <CardHeader>
                <InfoTooltip content="KPI details table showing: KPI Name, Target, Actual, Variance, Trend. Formula: Variance = Actual - Target. Positive variance (green +) = beat target for revenue/profit. Negative variance (red -) = missed target. For costs: negative = good (saved money), positive = bad (overspent). Trend: ↑ improving, ↓ declining. Set SMART targets: Specific, Measurable, Achievable, Relevant, Time-bound.">
                  <CardTitle>KPI Details</CardTitle>
                </InfoTooltip>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold">KPI</th>
                        <th className="text-right p-3 font-semibold">Target</th>
                        <th className="text-right p-3 font-semibold">Actual</th>
                        <th className="text-right p-3 font-semibold">Variance</th>
                        <th className="text-center p-3 font-semibold">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpisData.kpi_analysis.map((kpi, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{kpi.kpi_name}</td>
                          <td className="p-3 text-right">
                            {kpi.unit === 'currency' ? formatCurrency(kpi.target) : `${kpi.target}${kpi.unit}`}
                          </td>
                          <td className="p-3 text-right font-bold">
                            {kpi.unit === 'currency' ? formatCurrency(kpi.actual) : `${kpi.actual.toFixed(1)}${kpi.unit}`}
                          </td>
                          <td className={`p-3 text-right ${kpi.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {kpi.variance >= 0 ? '+' : ''}
                            {kpi.unit === 'currency' ? formatCurrency(kpi.variance) : `${kpi.variance.toFixed(1)}${kpi.unit}`}
                          </td>
                          <td className="p-3 text-center">
                            {kpi.trend === 'up' ? (
                              <TrendingUp className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-600 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StrategicAlignment;
